import type { WorkspaceSummary } from "@sagot/api-client";
import { CONSOLE_PATHS, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  cn,
  initialsOf,
} from "@sagot/ui";
import { useLocation } from "react-router";
import { useWorkspaceContext, useWorkspaceDetails } from "../session/workspace-context";
import { agentHref, useActiveAgent, type AgentOption } from "./use-agents";

const MENU_WIDTH = "w-[232px]";
/** A long list (an owner of many workspaces, an agent roster) scrolls inside the space the browser leaves for the menu instead of running off the screen. */
const MENU_SCROLL = "max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto";

function WorkspaceBadge({ name, size, current }: { name: string; size: "sm" | "md"; current: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center justify-center font-extrabold",
        size === "md" ? "size-8 rounded-md text-xs" : "size-6 rounded-sm text-[10px]",
        current ? "bg-signal-500 text-ink-950" : "bg-ink-200 text-ink-800",
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/** "Standard plan · School" from `GET /v1/workspace` (plan + industry). */
function usePlanLine(): string | null {
  const { t } = useTranslation();
  const details = useWorkspaceDetails();
  if (!details.data) return null;
  const plan = t(`console.plans.${details.data.plan}`, { defaultValue: details.data.plan });
  const industry = details.data.industry ? t(`console.industries.${details.data.industry}`, { defaultValue: "" }) : "";
  return industry ? t("console.planLine", { plan, industry }) : t("console.planOnly", { plan });
}

function WorkspaceItem({ workspace, current }: { workspace: WorkspaceSummary; current: boolean }) {
  return (
    <DropdownMenuItem asChild className={cn("font-semibold", current && "bg-primary-soft")}>
      <AppLink href={consoleRoutes.dashboard(workspace.slug)} aria-current={current ? "true" : undefined}>
        <WorkspaceBadge name={workspace.name} size="sm" current={current} />
        <span className="truncate">{workspace.name}</span>
        {current ? <Icon name="check" className="ml-auto text-primary-ink" /> : null}
      </AppLink>
    </DropdownMenuItem>
  );
}

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { summary, me } = useWorkspaceContext();
  const planLine = usePlanLine();
  const workspaces = me.workspaces.filter((w) => w.membership_status === "active" || w.id === summary.id);
  return (
    <div className="px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`${t("shell.switchWorkspace")}: ${summary.name}`}
          className="flex w-full items-center gap-2.5 rounded-md border border-chrome-line bg-chrome-2 px-2.5 py-2 text-left transition-colors hover:bg-chrome-3"
        >
          <WorkspaceBadge name={summary.name} size="md" current />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-bold text-white">{summary.name}</span>
            {planLine ? <span className="block text-[11px] text-chrome-muted">{planLine}</span> : <Skeleton className="mt-1 h-3 w-24 bg-chrome-3" />}
          </span>
          <Icon name="chevrons-up-down" className="text-chrome-muted" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className={cn(MENU_WIDTH, MENU_SCROLL)}>
          <DropdownMenuLabel>{t("shell.workspaces")}</DropdownMenuLabel>
          {workspaces.map((workspace) => (
            <WorkspaceItem key={workspace.id} workspace={workspace} current={workspace.id === summary.id} />
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <AppLink href={`${CONSOLE_PATHS.onboarding}?new=1`}>
              <Icon name="plus" />
              {t("shell.newWorkspace")}
            </AppLink>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface AgentMenuProps {
  agents: readonly AgentOption[];
  active: AgentOption;
  ws: string;
  routeAgentId: string | null;
}

function AgentMenu({ agents, active, ws, routeAgentId }: AgentMenuProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <DropdownMenuContent className={cn(MENU_WIDTH, MENU_SCROLL)}>
      {agents.map((agent) => (
        <DropdownMenuItem key={agent.id} asChild className={cn("items-start", agent.id === active.id && "bg-primary-soft")}>
          <AppLink
            href={agentHref(pathname, ws, routeAgentId, agent.id, consoleRoutes.playground(ws, agent.id))}
            aria-current={agent.id === active.id ? "true" : undefined}
          >
            <Icon name="bot" className="mt-0.5 text-primary-ink" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{agent.name}</span>
              {agent.meta ? <span className="block truncate text-[11px] text-fg-subtle">{agent.meta}</span> : null}
            </span>
            {agent.id === active.id ? <Icon name="check" className="mt-0.5 text-primary-ink" /> : null}
          </AppLink>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild>
        <AppLink href={consoleRoutes.agents(ws)}>
          <Icon name="layers" />
          {t("shell.manageAgents")}
        </AppLink>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <AppLink href={`${consoleRoutes.agents(ws)}#new`}>
          <Icon name="plus" />
          {t("shell.createAgent")}
        </AppLink>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

const LIVE_DOT: Readonly<Record<AgentOption["status"], string>> = { live: "bg-mint-500", paused: "bg-signal-500", draft: "bg-ink-400" };

/** Active agent switcher (`GET /v1/agents`); the active agent lives in the URL (`:agentId`). */
export function AgentSwitcher({ agentId }: { agentId: string | null }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { state, active } = useActiveAgent(agentId);
  if (state.status === "loading") {
    return (
      <div className="px-3 pt-2" aria-hidden="true">
        <Skeleton className="mb-1 ml-1 h-2.5 w-20 bg-chrome-3" />
        <Skeleton className="h-10 w-full bg-chrome-3" />
      </div>
    );
  }
  if (!active) return null;
  return (
    <div className="px-3 pt-2">
      <div className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">{t("shell.activeAgent")}</div>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`${t("shell.switchAgent")}: ${active.name}`}
          className="flex w-full items-center gap-2 rounded-md bg-chrome-3 px-2.5 py-2 text-left transition-colors hover:bg-ink-700"
        >
          <span aria-hidden="true" className="relative inline-flex size-6 items-center justify-center rounded-sm bg-brand-500 text-ink-950">
            <Icon name="bot" className="size-3.5" />
            <span className={cn("absolute -bottom-0.5 -right-0.5 size-2 rounded-pill ring-2 ring-chrome-3", LIVE_DOT[active.status])} />
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-white">{active.name}</span>
          <span className="sr-only">{t(`build.status.${active.status}`)}</span>
          <Icon name="chevron-down" className="text-chrome-muted" />
        </DropdownMenuTrigger>
        <AgentMenu agents={state.agents} active={active} ws={summary.slug} routeAgentId={agentId} />
      </DropdownMenu>
    </div>
  );
}
